import React, { useState, useEffect } from 'react';
import {
  Table,
  Button,
  Tag,
  Space,
  message,
  Modal,
  Form,
  Select,
  DatePicker,
  Checkbox,
  Input
} from 'antd';
import { CheckOutlined, EyeOutlined } from '@ant-design/icons';
import deferredRevenueService, { DeferredRevenueScheduleLine } from '../../../../services/deferredRevenueService';
import dayjs from 'dayjs';

const { Option } = Select;

interface Props {
  deferredRevenueId: string;
  state: string;
  onRefresh: () => void;
}

const ScheduleLineTable: React.FC<Props> = ({ deferredRevenueId, state, onRefresh }) => {
  const [loading, setLoading] = useState(false);
  const [scheduleLines, setScheduleLines] = useState<DeferredRevenueScheduleLine[]>([]);
  const [selectedRowKeys, setSelectedRowKeys] = useState<string[]>([]);
  const [postModal, setPostModal] = useState(false);
  const [batchPostModal, setBatchPostModal] = useState(false);
  const [form] = Form.useForm();
  const [journals, setJournals] = useState<any[]>([]);

  useEffect(() => {
    fetchScheduleLines();
    fetchJournals();
  }, [deferredRevenueId]);

  const fetchScheduleLines = async () => {
    try {
      setLoading(true);
      const response = await deferredRevenueService.getScheduleLines(deferredRevenueId);
      if (response.success) {
        setScheduleLines(response.data);
      }
    } catch (error: any) {
      message.error(error.message || 'Failed to fetch schedule lines');
    } finally {
      setLoading(false);
    }
  };

  const fetchJournals = async () => {
    // TODO: Fetch journals from API
    setJournals([{ id: '1', code: 'GEN', name: 'General Journal' }]);
  };

  const handlePostSingle = async (lineId: string, values: any) => {
    try {
      const response = await deferredRevenueService.postScheduleLine(lineId, {
        journalId: values.journalId,
        accountingDate: values.accountingDate?.format('YYYY-MM-DD')
      });

      if (response.success) {
        message.success('Schedule line posted successfully');
        setPostModal(false);
        form.resetFields();
        fetchScheduleLines();
        onRefresh();
      }
    } catch (error: any) {
      message.error(error.message || 'Failed to post schedule line');
    }
  };

  const handleBatchPost = async (values: any) => {
    try {
      const response = await deferredRevenueService.batchPostScheduleLines(deferredRevenueId, {
        scheduleLineIds: selectedRowKeys,
        journalId: values.journalId,
        accountingDate: values.accountingDate?.format('YYYY-MM-DD')
      });

      if (response.success) {
        const { posted, errors } = response.data;
        if (errors.length > 0) {
          message.warning(`Posted ${posted} lines successfully. ${errors.length} failed.`);
        } else {
          message.success(`Posted ${posted} schedule lines successfully`);
        }
        setBatchPostModal(false);
        form.resetFields();
        setSelectedRowKeys([]);
        fetchScheduleLines();
        onRefresh();
      }
    } catch (error: any) {
      message.error(error.message || 'Failed to batch post schedule lines');
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const columns = [
    {
      title: 'Period Start',
      dataIndex: 'periodStartDate',
      key: 'periodStartDate',
      render: (date: string) => dayjs(date).format('YYYY-MM-DD')
    },
    {
      title: 'Period End',
      dataIndex: 'periodEndDate',
      key: 'periodEndDate',
      render: (date: string) => dayjs(date).format('YYYY-MM-DD')
    },
    {
      title: 'Amount',
      dataIndex: 'amount',
      key: 'amount',
      align: 'right' as const,
      render: formatCurrency
    },
    {
      title: 'State',
      dataIndex: 'state',
      key: 'state',
      render: (state: string) => (
        <Tag color={state === 'POSTED' ? 'green' : 'default'}>
          {state}
        </Tag>
      )
    },
    {
      title: 'Posted Date',
      dataIndex: 'postedAt',
      key: 'postedAt',
      render: (date: string) => date ? dayjs(date).format('YYYY-MM-DD HH:mm') : '-'
    },
    {
      title: 'Posted By',
      dataIndex: 'postedByUser',
      key: 'postedByUser',
      render: (user: any) => user?.name || '-'
    },
    {
      title: 'Journal Entry',
      dataIndex: 'journalEntryId',
      key: 'journalEntryId',
      render: (journalEntryId: string, record: DeferredRevenueScheduleLine) =>
        journalEntryId ? (
          <Button
            type="link"
            size="small"
            icon={<EyeOutlined />}
            onClick={() => {
              // TODO: Open journal entry details
              message.info(`View Journal Entry: ${journalEntryId}`);
            }}
          >
            {record.journalEntry?.name || 'View'}
          </Button>
        ) : '-'
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (record: DeferredRevenueScheduleLine) =>
        record.state === 'DRAFT' && state === 'RUNNING' ? (
          <Button
            type="link"
            size="small"
            icon={<CheckOutlined />}
            onClick={() => {
              form.setFieldsValue({ lineId: record.id });
              setPostModal(true);
            }}
          >
            Post
          </Button>
        ) : null
    }
  ];

  const rowSelection = {
    selectedRowKeys,
    onChange: (selectedKeys: React.Key[]) => {
      setSelectedRowKeys(selectedKeys as string[]);
    },
    getCheckboxProps: (record: DeferredRevenueScheduleLine) => ({
      disabled: record.state === 'POSTED'
    })
  };

  const unpostedLines = scheduleLines.filter((line) => line.state === 'DRAFT');

  return (
    <div>
      <div style={{ marginBottom: 16 }}>
        <Space>
          {state === 'RUNNING' && unpostedLines.length > 0 && (
            <Button
              type="primary"
              onClick={() => setBatchPostModal(true)}
              disabled={selectedRowKeys.length === 0}
            >
              Batch Post ({selectedRowKeys.length} selected)
            </Button>
          )}
          <Button onClick={fetchScheduleLines}>Refresh</Button>
        </Space>
      </div>

      <Table
        columns={columns}
        dataSource={scheduleLines}
        loading={loading}
        rowKey="id"
        rowSelection={state === 'RUNNING' ? rowSelection : undefined}
        pagination={false}
        summary={(pageData) => {
          const totalAmount = pageData.reduce((sum, line) => sum + Number(line.amount), 0);
          const postedAmount = pageData
            .filter((line) => line.state === 'POSTED')
            .reduce((sum, line) => sum + Number(line.amount), 0);

          return (
            <>
              <Table.Summary.Row>
                <Table.Summary.Cell index={0} colSpan={2}>
                  <strong>Total</strong>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={2} align="right">
                  <strong>{formatCurrency(totalAmount)}</strong>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={3} colSpan={4} />
              </Table.Summary.Row>
              <Table.Summary.Row>
                <Table.Summary.Cell index={0} colSpan={2}>
                  <strong>Posted</strong>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={2} align="right">
                  <strong>{formatCurrency(postedAmount)}</strong>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={3} colSpan={4} />
              </Table.Summary.Row>
              <Table.Summary.Row>
                <Table.Summary.Cell index={0} colSpan={2}>
                  <strong>Remaining</strong>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={2} align="right">
                  <strong>{formatCurrency(totalAmount - postedAmount)}</strong>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={3} colSpan={4} />
              </Table.Summary.Row>
            </>
          );
        }}
      />

      {/* Post Single Line Modal */}
      <Modal
        title="Post Schedule Line"
        open={postModal}
        onOk={() => {
          const lineId = form.getFieldValue('lineId');
          form.validateFields().then((values) => {
            handlePostSingle(lineId, values);
          });
        }}
        onCancel={() => {
          setPostModal(false);
          form.resetFields();
        }}
      >
        <Form form={form} layout="vertical">
          <Form.Item name="lineId" hidden>
            <Input />
          </Form.Item>
          <Form.Item
            name="journalId"
            label="Journal"
            rules={[{ required: true, message: 'Please select a journal' }]}
          >
            <Select placeholder="Select journal">
              {journals.map((j) => (
                <Option key={j.id} value={j.id}>
                  {j.code} - {j.name}
                </Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item name="accountingDate" label="Accounting Date">
            <DatePicker style={{ width: '100%' }} />
          </Form.Item>
        </Form>
      </Modal>

      {/* Batch Post Modal */}
      <Modal
        title={`Batch Post Schedule Lines (${selectedRowKeys.length} selected)`}
        open={batchPostModal}
        onOk={() => form.submit()}
        onCancel={() => {
          setBatchPostModal(false);
          form.resetFields();
        }}
      >
        <Form form={form} layout="vertical" onFinish={handleBatchPost}>
          <Form.Item
            name="journalId"
            label="Journal"
            rules={[{ required: true, message: 'Please select a journal' }]}
          >
            <Select placeholder="Select journal">
              {journals.map((j) => (
                <Option key={j.id} value={j.id}>
                  {j.code} - {j.name}
                </Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item name="accountingDate" label="Accounting Date">
            <DatePicker style={{ width: '100%' }} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default ScheduleLineTable;
